import { useState } from 'react';

import { FieldError } from '#/components/Form/Field/FieldError';
import { Input } from '#/components/Input/Input';
import type { I18nLocale } from '#/i18n/types/I18nLocale';
import { CURRENCIES } from '#/utils/constants/currency';

import {
  CurrencyInputContainer,
  CurrencyInputSign,
} from './CurrencyInput.styled';
import { currencyInputDelimiterGetMaxDecimalPlaces } from './delimiter/currencyInputDelimiterGetMaxDecimalPlaces';
import { useCurrencyInputDelimiter } from './delimiter/useCurrencyInputDelimiter';

type Props = {
  allowDecimal?: boolean;
  currencyCode?: keyof typeof CURRENCIES;
  errorMessage?: string | null;
  id?: string;
  max?: number;
  min?: number;
  name?: string;
  onChange?: (changeValue: string) => void;
  placeholder?: string;
  required?: boolean;
  step?: number;
  value?: number | string;
  locale: I18nLocale;
  qaId?: string;
};

export function CurrencyInput({
  allowDecimal,
  id,
  name,
  value,
  placeholder,
  errorMessage,
  min,
  max,
  step,
  onChange,
  required,
  currencyCode,
  locale,
  qaId,
}: Props) {
  const currency = currencyCode && CURRENCIES[currencyCode];
  const parsedValue = (() => {
    const numericValue = typeof value === 'string' ? Number(value) : value;
    return Number.isFinite(numericValue) ? numericValue : undefined;
  })();

  const [touched, setTouched] = useState(false);

  const {
    inputRef,
    displayValue,
    onChangeHandler,
    onBlurHandler,
    onKeyDownHandler,
  } = useCurrencyInputDelimiter({
    allowDecimal: Boolean(allowDecimal),
    maxDecimalPlaces: currencyInputDelimiterGetMaxDecimalPlaces({
      locale,
      currencyCode,
    }),
    min: min ?? 0,
    max,
    value: parsedValue,
    onChange,
    onBlur: () => {
      if (!touched) setTouched(true);
    },
    step: step ?? 1,
    locale,
  });

  return (
    <CurrencyInputContainer>
      <div style={{ width: '100%' }}>
        <Input
          fullWidth
          inputProps={{
            qaId,
            ref: inputRef,
            onChange: onChangeHandler,
            value: displayValue,
            name: `${name ?? 'a'}`,
            id,
            inputMode: 'decimal',
            step,
            onBlur: onBlurHandler,
            onKeyDown: onKeyDownHandler,
            placeholder,
            required,
          }}
          prefix={
            currency ? (
              <CurrencyInputSign>{currency.sign}</CurrencyInputSign>
            ) : null
          }
          affixVariant="transparent"
        />

        {errorMessage && touched && (
          <FieldError errorMessage={errorMessage} inputId={id} />
        )}
      </div>
    </CurrencyInputContainer>
  );
}
